import React, { useEffect } from 'react';
import { useStrategyOperations } from '../hooks/useStrategyOperations';
import { TradingSignal, SignalAction } from '../types';
import config from '../config';

const Dashboard: React.FC = () => {
  const { signals, loading, error, getLatestSignals } = useStrategyOperations();

  useEffect(() => {
    // Log to help with debugging
    console.log('Dashboard: Fetching trading signals...');
    console.log('Using real API:', config.useRealApi, 'Mock API:', config.useMockApi);
    
    // Force a refresh of the signals when the dashboard loads
    // Using type assertion to bypass TypeScript error
    getLatestSignals(true as any).then(() => {
      console.log('Dashboard: Signals fetched successfully');
    });
  }, [getLatestSignals]);

  // Log signals for debugging
  useEffect(() => {
    console.log('Current signals:', signals);
    // Count by type for debugging
    console.log('Signal counts:', {
      buy: signals.filter(s => s.finalSignal?.toUpperCase() === 'BUY').length,
      sell: signals.filter(s => s.finalSignal?.toUpperCase() === 'SELL').length,
      hold: signals.filter(s => s.finalSignal?.toUpperCase() === 'HOLD').length,
      total: signals.length
    });
  }, [signals]);

  // Count signals by type
  const buySignals = signals.filter(s => s.finalSignal?.toUpperCase() === 'BUY').length;
  const sellSignals = signals.filter(s => s.finalSignal?.toUpperCase() === 'SELL').length;
  const holdSignals = signals.filter(s => s.finalSignal?.toUpperCase() === 'HOLD').length;

  // Get all buy and sell signals
  const allBuySignals = signals
    .filter(s => s.finalSignal?.toUpperCase() === 'BUY')
    .sort((a, b) => (a.cape || 0) - (b.cape || 0))
    .slice(0, 100); // Show up to 100 buy signals

  const allSellSignals = signals
    .filter(s => s.finalSignal?.toUpperCase() === 'SELL')
    .sort((a, b) => (b.cape || 0) - (a.cape || 0))
    .slice(0, 100); // Show up to 100 sell signals

  // Helper function to get background color based on signal type
  const getSignalColor = (signal?: SignalAction): string => {
    const signalUpper = signal?.toUpperCase();
    switch (signalUpper) {
      case 'BUY':
        return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100';
      case 'SELL':
        return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-10">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <p className="mt-2 text-primary-600">Loading signals...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mt-4 dark:bg-red-900 dark:border-red-600 dark:text-red-100">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400 dark:text-red-300" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Dashboard</h1>
        <button
          onClick={() => getLatestSignals(true as any)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          Refresh Signals
        </button>
      </div>

      {/* Signal summary cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        {/* Buy Signals Card */}
        <div className="card border-l-4 border-green-500 p-4 bg-white dark:bg-gray-800 shadow rounded">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 dark:bg-green-800 mr-4">
              <svg className="h-6 w-6 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
              </svg>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 dark:text-gray-300">Buy Signals</div>
              <div className="text-3xl font-semibold text-gray-900 dark:text-white">{buySignals}</div>
            </div>
          </div>
        </div>

        {/* Sell Signals Card */}
        <div className="card border-l-4 border-red-500 p-4 bg-white dark:bg-gray-800 shadow rounded">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 dark:bg-red-800 mr-4">
              <svg className="h-6 w-6 text-red-600 dark:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
              </svg>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 dark:text-gray-300">Sell Signals</div>
              <div className="text-3xl font-semibold text-gray-900 dark:text-white">{sellSignals}</div>
            </div>
          </div>
        </div>

        {/* Hold Signals Card */}
        <div className="card border-l-4 border-gray-500 p-4 bg-white dark:bg-gray-800 shadow rounded">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-gray-100 dark:bg-gray-700 mr-4">
              <svg className="h-6 w-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14"></path>
              </svg>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-500 dark:text-gray-300">Hold Signals</div>
              <div className="text-3xl font-semibold text-gray-900 dark:text-white">{holdSignals}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Signals tables with fixed height and scrolling */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Buy Signals */}
        <div className="card p-4 bg-white dark:bg-gray-800 shadow rounded">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Top Buy Signals</h2>
          {allBuySignals.length > 0 ? (
            <div className="overflow-x-auto h-80 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ticker</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">CAPE Value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">F-Score</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Signal</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {allBuySignals.map((signal) => (
                    <tr key={signal.ticker}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{signal.ticker}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {signal.cape !== undefined ? signal.cape.toFixed(2) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {signal.fScore && typeof signal.fScore === 'object' && 'score' in signal.fScore 
                          ? `${signal.fScore.score}/9` 
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSignalColor(signal.finalSignal)}`}>
                          {signal.finalSignal ? signal.finalSignal.toUpperCase() : 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-300">No buy signals available</p>
          )}
        </div>

        {/* Sell Signals */}
        <div className="card p-4 bg-white dark:bg-gray-800 shadow rounded">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Top Sell Signals</h2>
          {allSellSignals.length > 0 ? (
            <div className="overflow-x-auto h-80 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ticker</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">CAPE Value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">F-Score</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Signal</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {allSellSignals.map((signal) => (
                    <tr key={signal.ticker}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{signal.ticker}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {signal.cape !== undefined ? signal.cape.toFixed(2) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                        {signal.fScore && typeof signal.fScore === 'object' && 'score' in signal.fScore 
                          ? `${signal.fScore.score}/9` 
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSignalColor(signal.finalSignal)}`}>
                          {signal.finalSignal ? signal.finalSignal.toUpperCase() : 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-300">No sell signals available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 